import React, { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { AlertTriangle, ArrowLeft, Copy } from 'lucide-react'
import { apiDelete, apiGet, apiPatch, apiPost } from '../api'
import { Alert, Loading, StatusPill, Tabs, tournamentStatus } from '../components/ui'
import { FORMAT_LABELS, readError } from './tournament/shared'
import OverviewTab from './tournament/OverviewTab'
import SetupTab from './tournament/SetupTab'
import SettingsTab from './tournament/SettingsTab'
import TeamsTab from './tournament/TeamsTab'
import MatchesTab from './tournament/MatchesTab'
import AdminsTab from './tournament/AdminsTab'
import ProductsTab from './tournament/ProductsTab'
import './tournament.css'

const TABS = [
  { key: 'oversikt', label: 'Oversikt' },
  { key: 'oppsett', label: 'Oppsett' },
  { key: 'innstillinger', label: 'Innstillinger' },
  { key: 'lag', label: 'Lag' },
  { key: 'kamper', label: 'Kamper' },
  { key: 'admins', label: 'Admins' },
  { key: 'produkter', label: 'Produkter og salg' }
]
const TAB_KEYS = TABS.map((t) => t.key)

function TournamentDashboardPage() {
  const { tournamentId } = useParams()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [tournament, setTournament] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const tabParam = searchParams.get('tab')
  const tab = TAB_KEYS.includes(tabParam) ? tabParam : 'oversikt'
  const setTab = useCallback((key) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      if (key === 'oversikt') next.delete('tab')
      else next.set('tab', key)
      return next
    }, { replace: true })
  }, [setSearchParams])

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const res = await apiGet(`/api/tournaments/${tournamentId}`)
      if (!res.ok) {
        setError(await readError(res, 'Kunne ikke hente turnering'))
        setTournament(null)
        return
      }
      setTournament(await res.json())
    } catch (err) {
      setError(err.message || 'Nettverksfeil')
    } finally {
      setLoading(false)
    }
  }, [tournamentId])

  useEffect(() => {
    load()
  }, [load])

  // Hold aktiv fane synlig i den horisontalt rullbare fanelinjen (mobil).
  const hasTournament = !!tournament
  useEffect(() => {
    if (!hasTournament) return
    const el = document.querySelector('.tn-top .tab[aria-selected="true"]')
    const bar = el?.parentElement
    if (el && bar && bar.scrollWidth > bar.clientWidth) {
      bar.scrollLeft = el.offsetLeft - (bar.clientWidth - el.offsetWidth) / 2
    }
  }, [tab, hasTournament])

  const flash = (msg) => {
    setSuccess(msg)
    setTimeout(() => setSuccess(''), 2500)
  }

  const run = async (action, okMsg) => {
    setSaving(true)
    setError('')
    try {
      const result = await action()
      if (result === false) return
      if (result?.deleted) {
        navigate('/tournaments')
        return
      }
      if (result?.tournament) setTournament(result.tournament)
      else await load()
      if (okMsg) flash(okMsg)
    } catch (err) {
      setError(err.message || 'Noe gikk galt')
    } finally {
      setSaving(false)
    }
  }

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(tournament.inviteCode)
      flash('Kode kopiert')
    } catch (_) {
      setError('Kunne ikke kopiere koden')
    }
  }

  if (loading && !tournament) {
    return (
      <div className="page">
        <Loading>Laster turnering…</Loading>
      </div>
    )
  }

  if (!tournament) {
    return (
      <div className="page">
        <Alert tone="error">{error || 'Fant ikke turneringen'}</Alert>
        <div>
          <Link className="btn btn-tertiary" to="/tournaments">
            <ArrowLeft size={18} strokeWidth={1.9} aria-hidden="true" />Tilbake
          </Link>
        </div>
      </div>
    )
  }

  const canManage = tournament.canManage === true
  const status = tournamentStatus(tournament.status)

  return (
    <div className="page tn-page">
      <div className="tn-top">
        <nav aria-label="Brødsmuler" className="tn-crumbs">
          <Link to="/tournaments">Turneringer</Link>
          <span aria-hidden="true">/</span>
          <span aria-current="page">{tournament.name}</span>
        </nav>
        <div className="tn-hero">
          <div className="tn-hero-main">
            <Link to="/tournaments" className="tn-back">
              <ArrowLeft size={16} strokeWidth={2} aria-hidden="true" />Tilbake
            </Link>
            <div className="tn-title-row">
              <h1 className="page-header-title">{tournament.name}</h1>
              <StatusPill tone={status.tone} dot large>{status.label}</StatusPill>
            </div>
            <div className="tn-chips">
              {tournament.sportType && <span className="chip">{tournament.sportType}</span>}
              <span className="chip">{FORMAT_LABELS[tournament.format] || tournament.format}</span>
              <span className="chip">{tournament.teamCount} lag · {tournament.matchCount} kamper</span>
            </div>
          </div>
          {tournament.inviteCode && (
            <div className="tn-code-box">
              <span className="tn-code-label">Kode:</span>
              <span className="tn-code-value">{tournament.inviteCode}</span>
              <button type="button" className="tn-code-copy" aria-label="Kopier kode" title="Kopier kode" onClick={copyCode}>
                <Copy size={16} strokeWidth={1.9} aria-hidden="true" />
              </button>
            </div>
          )}
        </div>
        {!canManage && (
          <div role="note" className="tn-noadmin">
            <AlertTriangle size={18} strokeWidth={2} aria-hidden="true" />
            <span>Du har ikke admin-tilgang til denne turneringen.</span>
          </div>
        )}
        {error && <Alert tone="error">{error}</Alert>}
        {success && <Alert tone="success">{success}</Alert>}
        <Tabs tabs={TABS} active={tab} onChange={setTab} label="Turneringsfaner" />
      </div>

      <div className="tn-panel" role="tabpanel" aria-label={TABS.find((t) => t.key === tab)?.label}>
        {tab === 'oversikt' && <OverviewTab tournament={tournament} onGoTab={setTab} />}
        {tab === 'oppsett' && (
          <SetupTab
            tournament={tournament}
            canManage={canManage}
            saving={saving}
            run={run}
            onReload={load}
            success={success}
          />
        )}
        {tab === 'innstillinger' && (
          <SettingsTab
            tournament={tournament}
            canManage={canManage}
            saving={saving}
            justSaved={success === 'Innstillinger lagret'}
            onSave={(body) => run(async () => {
              const res = await apiPatch(`/api/tournaments/${tournament.id}`, body)
              if (!res.ok) throw new Error(await readError(res, 'Kunne ikke lagre'))
              return { tournament: await res.json() }
            }, 'Innstillinger lagret')}
            onRegenerate={() => run(async () => {
              const res = await apiPost(`/api/tournaments/${tournament.id}/regenerate-bracket?draw=true`)
              if (!res.ok) throw new Error(await readError(res, 'Kunne ikke trekke kampoppsett'))
              return { tournament: await res.json() }
            }, 'Nytt kampoppsett trukket')}
            onCancel={() => run(async () => {
              const res = await apiPost(`/api/tournaments/${tournament.id}/cancel`)
              if (!res.ok) throw new Error(await readError(res, 'Kunne ikke avlyse'))
              return { tournament: await res.json() }
            }, 'Turnering avlyst')}
            onDelete={() => run(async () => {
              const res = await apiDelete(`/api/tournaments/${tournament.id}`)
              if (!res.ok) throw new Error(await readError(res, 'Kunne ikke slette'))
              return { deleted: true }
            })}
          />
        )}
        {tab === 'lag' && (
          <TeamsTab
            tournament={tournament}
            canManage={canManage}
            saving={saving}
            run={run}
            setError={setError}
          />
        )}
        {tab === 'kamper' && (
          <MatchesTab
            tournament={tournament}
            canManage={canManage}
            saving={saving}
            run={run}
            onReload={load}
          />
        )}
        {tab === 'admins' && (
          <AdminsTab
            tournament={tournament}
            canManage={canManage}
            saving={saving}
            run={run}
            setError={setError}
          />
        )}
        {tab === 'produkter' && <ProductsTab tournament={tournament} />}
      </div>
    </div>
  )
}

export default TournamentDashboardPage
